import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { ProdutoService } from "almoxarifado-lib";
import { Login } from "eddydata-lib";
import * as XLSX from 'xlsx';
import * as toastr from 'toastr';

declare const $: any;

type ImportacaoRegistro = {
  nome?: any;
  grupo?: any;
  descricao?: any;
  unidade?: any;
};

interface LinhaConvertida {
  line: number;
  nome: string;
  ge: string;
  sge: string;
  m: string;
  descricao: string;
  unidade: string;
  hashKey: string;
}

interface LinhaInvalida {
  line: number;
  erros: string[];
  raw: ImportacaoRegistro;
}

@Component({
  selector: 'lib-produto-importacao',
  templateUrl: './produto-importacao.component.html'
})
export class ProdutoImportacaoComponent {

  public importacaoResultado: any;
  public carregandoImportacao = false;
  public importacao: {
    total: number;
    validos: LinhaConvertida[];
    invalidos: LinhaInvalida[];
    duplicados: number[];
    payload?: any[];
  } | null = null;

  @Input('login') login: Login;
  @Input('servico') servico: Boolean = false;
  @Output() callback: EventEmitter<void> = new EventEmitter();
  @ViewChild('filePlanilha') filePlanilha!: ElementRef<HTMLInputElement>;

  constructor(
    protected produtoService: ProdutoService,
  ) { }


  baixarModeloImportacao(): void {
    const data = [
      ['NOME', 'GRUPO', 'DESCRIÇÃO', 'UNIDADE DE FORNECIMENTO']
    ];

    const ws = XLSX.utils.aoa_to_sheet(data);

    (ws as any)['!cols'] = [
      { wch: 50 },
      { wch: 14 },
      { wch: 70 },
      { wch: 30 }
    ];

    (ws as any)['!freeze'] = { xSplit: 0, ySplit: 1 };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Modelo');

    const fileName = `modelo_importacao_${this.servico ? 'servicos' : 'produto'}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }

  abrirSelecaoPlanilha(): void {
    if (this.filePlanilha?.nativeElement) {
      this.filePlanilha.nativeElement.value = '';
      this.filePlanilha.nativeElement.click();
    }
  }

  onArquivoPlanilhaSelecionado(evt: Event): void {
    const input = evt.target as HTMLInputElement;
    const file = input?.files && input.files[0];
    if (!file) return;

    this.carregandoImportacao = true;
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const data = new Uint8Array(reader.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const wsName = wb.SheetNames[0];
        const ws = wb.Sheets[wsName];

        const rows = XLSX.utils.sheet_to_json<ImportacaoRegistro>(ws, { defval: '', raw: false });

        const validos: LinhaConvertida[] = [];
        const invalidos: LinhaInvalida[] = [];
        const vistos = new Set<string>();
        const duplicados: number[] = [];

        rows.forEach((r, i) => {
          const lineNumber = i + 2;

          const nome = this.getCell(r, ['NOME']);
          const mp = this.getCell(r, ['GRUPO']);
          const desc = this.getCell(r, ['DESCRIÇÃO']);
          const und = this.getCell(r, ['UNIDADE DE FORNECIMENTO']);

          if (!nome && !mp && !desc && !und) return;

          const errors: string[] = [];
          if (!nome) errors.push('Campo "Nome" obrigatório.');
          if (!mp) errors.push('Campo "Grupo" obrigatório (formato GE.SGE.M).');
          if (!und) errors.push('Campo "Unidade de fornecimento" obrigatório.');

          const parsed = mp ? this.parseGrupo(mp) : null;
          if (mp && !parsed) errors.push('Grupo inválido. Use o formato GE.SGE.M (ex.: 10.20.003).');

          if (errors.length) {
            invalidos.push({ line: lineNumber, erros: errors, raw: r });
            return;
          }

          const row: LinhaConvertida = {
            line: lineNumber,
            nome,
            ge: parsed!.ge,
            sge: parsed!.sge,
            m: parsed!.m,
            descricao: desc,
            unidade: und,
            hashKey: (nome.toLowerCase().trim() + '|' + parsed!.ge + '.' + parsed!.sge + '.' + parsed!.m)
          };

          if (vistos.has(row.hashKey)) {
            duplicados.push(lineNumber);
            invalidos.push({ line: lineNumber, erros: ['Duplicado na planilha (mesmo nome + grupo).'], raw: r });
            return;
          }

          vistos.add(row.hashKey);
          validos.push(row);
        });

        const payload = validos.map(v => ({
          nome: v.nome,
          descricao: v.descricao,
          unidadeNome: v.unidade,
          geCodigo: v.ge,
          sgeCodigo: v.sge,
          materialCodigo: v.m,
          servico: this.servico
        }));

        this.importacao = {
          total: rows.length,
          validos,
          invalidos,
          duplicados,
          payload
        };

        $('#dialogImportacao').modal('show');
      } catch (err) {
        console.error('Erro ao ler planilha:', err);
        this.importacao = null;
        alert('Não foi possível ler a planilha. Verifique o arquivo e tente novamente.');
      } finally {
        this.carregandoImportacao = false;
      }
    };

    reader.readAsArrayBuffer(file);
  }

  private getCell(obj: ImportacaoRegistro, keys: string[]): string {
    for (const k of keys) {
      if (obj[k] !== undefined) return this.normalizar(obj[k]);
    }
    return '';
  }

  private normalizar(v: any): string {
    if (v === null || v === undefined) return '';
    return String(v).trim();
  }

  private parseGrupo(mp: string): { ge: string; sge: string; m: string } | null {
    if (!mp) return null;
    const s = String(mp).trim();
    const m = s.match(/^\s*([^.]+)\.([^.]+)\.([^.]+)\s*$/);
    if (!m) return null;
    return { ge: m[1], sge: m[2], m: m[3] };
  }

  confirmarImportacao(): void {
    if (!this.importacao?.payload?.length) return;

    this.carregandoImportacao = true;

    this.produtoService.importarPlanilha(this.login.orgao.id, this.importacao.payload)
      .subscribe({
        next: (res) => {
          this.carregandoImportacao = false;
          this.importacaoResultado = res;

          ($('#dialogImportacao') as any).modal('hide');

          ($('#dialogRelatorioImportacao') as any).modal('show');

          this.callback.emit();
        },
        error: (err) => {
          this.carregandoImportacao = false;
          console.error('Erro na importação:', err);
          toastr.error('Erro ao importar planilha. Verifique o arquivo e tente novamente.');
        }
      });
  }

  exportarRelatorioImportacao(): void {
    if (!this.importacaoResultado) { return; }

    const resumoData = [
      ['Processados', this.importacaoResultado.processados],
      ['Inseridos', this.importacaoResultado.inseridos],
      ['Já existentes', this.importacaoResultado.existentes],
      ['Erros', this.importacaoResultado.erros],
    ];
    const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);
    (wsResumo as any)['!cols'] = [{ wch: 20 }, { wch: 12 }];

    const header = ['#', 'Código', 'Status', 'Nome', 'Grupo'];
    const linhas = (this.importacaoResultado.codigos || []).map((c, i) => ([
      i + 1,
      c.codigo,
      c.criado ? 'Incluído' : 'Existente',
      c.nome,
      c.grupo
    ]));
    const wsCodigos = XLSX.utils.aoa_to_sheet([header, ...linhas]);

    (wsCodigos as any)['!cols'] = [
      { wch: 5 },
      { wch: 12 },
      { wch: 12 },
      { wch: 50 },
      { wch: 18 }
    ];
    (wsCodigos as any)['!freeze'] = { xSplit: 0, ySplit: 1 };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo');
    XLSX.utils.book_append_sheet(wb, wsCodigos, 'Códigos');

    const data = new Date();
    const yyyy = data.getFullYear();
    const mm = String(data.getMonth() + 1).padStart(2, '0');
    const dd = String(data.getDate()).padStart(2, '0');

    XLSX.writeFile(wb, `relatorio_importacao_${this.servico ? 'servicos' : 'produtos'}_${yyyy}-${mm}-${dd}.xlsx`);
  }
}